import { CaretLeft, X } from "@phosphor-icons/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type HTMLAttributes,
} from "react";

import { Button } from "common/components/ui/Button";
import { cn } from "common/helpers/utils";

interface DialogProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {
  onAfterClose?: () => void;
}

function Dialog({ onAfterClose = null, onOpenChange, ...props }: DialogProps) {
  return (
    <DialogPrimitive.Root
      onOpenChange={(open) => {
        if (onOpenChange) {
          onOpenChange(open);
        }

        if (!open && onAfterClose) {
          setTimeout(() => onAfterClose(), 300);
        }
      }}
      {...props}
    />
  );
}
Dialog.displayName = DialogPrimitive.Root.displayName;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogPrimitive.Overlay
      onClick={(e) => e.stopPropagation()}
      className="fixed inset-0 z-overlay bg-black-950/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    />
    <DialogPrimitive.Content
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "max-h-[90%] fixed flex flex-col left-1/2 top-1/2 z-dialog -translate-x-1/2 -translate-y-1/2 w-10/12 md:w-[540px] bg-white px-4 pb-6 pt-4 shadow-lg rounded-5xl duration-200 focus:outline-none",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className,
      )}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        (e.target as HTMLDivElement).focus();
      }}
      {...props}
    >
      {children}
      <Button asChild size="sm" variant="ghost-black" intent="iconOnly">
        <DialogClose id="close-button" className="absolute right-4 top-4">
          <X size={20} />
          <span className="sr-only">Close</span>
        </DialogClose>
      </Button>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const OverlayDialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogPrimitive.Overlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed flex flex-col inset-x-0 top-0 z-dialog bg-white duration-200 focus:outline-none h-dvh overflow-y-auto",
        className,
      )}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        (e.target as HTMLDivElement).focus();
      }}
      {...props}
    >
      {children}
      <Button asChild variant="secondary-black" intent="iconOnly">
        <DialogClose id="close-button" className="absolute left-4 top-3">
          <CaretLeft size={20} />
          <span className="sr-only">Close</span>
        </DialogClose>
      </Button>
    </DialogPrimitive.Content>
  </DialogPortal>
));
OverlayDialogContent.displayName = "OverlayDialogContent";

function OverlayDialogTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogPrimitive.Title
      className={cn("px-14 py-4 text-center text-xl-bold", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  );
}
function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex justify-between bg-white p-4 -mb-6 -mx-4 rounded-b-3xl border-t border-t-black-200",
        className,
      )}
      {...props}
    />
  );
}
DialogFooter.displayName = "DialogFooter";

function DialogTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogPrimitive.Title
      className={cn("px-10 text-center text-xl-bold", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  );
}

function DialogDescription({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogPrimitive.Description
      className={cn("mt-2 px-10 text-center text-sm text-black-500", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Description>
  );
}

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogClose,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  OverlayDialogContent,
  OverlayDialogTitle,
};
